<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { SearchBar, SearchToolbar, ColoredButton, SelectInput } from '@/shared/ui';
  import inboxIcon from '@/shared/assets/icons/Inbox.svg';
  import type { NotificationSearchType } from '../model/types';
  import exportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';

  interface Props {
    disabled?: boolean;
    canCreateNotification: boolean;
    initialSearchTerm?: string;
    initialSearchType?: NotificationSearchType;
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    initialSearchTerm: '',
    initialSearchType: 'notifications',
  });

  defineSlots<{
    quantity?(): unknown;
  }>();

  const emit = defineEmits([
    'search',
    'create-notification',
    'search-type-change',
    'export-to-excel',
  ]);

  const searchTypeOptions: Array<{ id: NotificationSearchType; name: string }> = [
    { id: 'notifications', name: 'Notificações' },
    { id: 'recipient', name: 'Destinatários' },
  ];

  const selectedSearchType = ref<NotificationSearchType>(props.initialSearchType);

  watch(
    () => props.initialSearchType,
    (value) => {
      selectedSearchType.value = value;
    }
  );

  const handleSearch = (queryString: string) => {
    emit('search', queryString);
  };

  const handleCreateNotification = () => {
    emit('create-notification');
  };

  const handleSearchTypeChange = (value: string | number) => {
    selectedSearchType.value = value as NotificationSearchType;
    emit('search-type-change', value);
  };

  const handleExportToExcel = () => {
    if (!props.disabled) {
      emit('export-to-excel');
    }
  };
</script>

<template>
  <div class="flex flex-col gap-4 w-full mb-6">
    <div class="flex gap-4 items-center w-full">
      <ColoredButton
        v-if="canCreateNotification"
        text="Criar notificação"
        :icon="inboxIcon"
        alt-icon="Criar notificação"
        @click="handleCreateNotification"
      />
      <ColoredButton
        text="Exportar para Excel"
        :icon="exportExcelIcon"
        alt-icon="Exportar para Excel"
        :disabled="disabled"
        @click="handleExportToExcel"
      />
    </div>

    <SearchToolbar>
      <template #search>
        <div
          class="flex gap-0 items-center bg-white-01 border-gray-02 border rounded-lg h-[42px] overflow-visible"
        >
          <div class="h-full overflow-visible">
            <SelectInput
              :model-value="selectedSearchType"
              :options="searchTypeOptions"
              container-class="border-0 bg-transparent h-full w-auto min-w-[140px] overflow-visible"
              button-class="bg-transparent border-gray-02 border-0 border-r rounded-none rounded-l-lg h-full px-4"
              :arrow-right="'right-3'"
              @update:model-value="handleSearchTypeChange"
            />
          </div>
          <div class="flex-1 h-full">
            <SearchBar
              :initial-search-term="props.initialSearchTerm"
              no-container
              @search="handleSearch"
            />
          </div>
        </div>
      </template>
      <template
        v-if="$slots.quantity"
        #quantity
      >
        <slot name="quantity" />
      </template>
    </SearchToolbar>
  </div>
</template>
